'use client';

import { useMicrocopy } from '@contexts/MicrocopyContext';
import { MicrocopyFragment } from '@graphql/generated-contentful/graphql';
import { microcopySetToResourceBundles } from '@lib/coveo/microcopySetToResourceBundles';
import { MicrocopyItem } from '@lib/types/cms/MicrocopySet';
import { useCallback, useMemo } from 'react';
import { TreeAccordion } from 'ui-components';
import { FacetAccordion } from '../FacetAccordion';
import { FacetDropdown } from '../FacetDropdown';
import { DATE_RANGE_KEYS, getReleaseDateController } from '../facetControllers';
import { DateFlatFacetValue, useFlatFacet } from '../hooks/useFlatFacet';
import { ReleaseDateFacetProps } from './ReleaseDateFacet.types';

const getValueId = (v: DateFlatFacetValue): string => `${v.start}-${v.end}`;

export const ReleaseDateFacet = ({
  variant = 'horizontal',
  className,
  isOpen,
  onToggle,
}: ReleaseDateFacetProps) => {
  const { searchLabelsMicrocopy, searchValuesMicrocopy } = useMicrocopy<{
    searchLabelsMicrocopy: MicrocopyFragment[];
    searchValuesMicrocopy: MicrocopyItem[];
  }>();

  const dateLabel =
    searchLabelsMicrocopy?.find(
      (m) => m.key === 'search.filter-release-date-label'
    )?.value ?? 'Release Date';

  const clearLabel =
    searchLabelsMicrocopy?.find((m) => m.key === 'search.clear')?.value ??
    'Clear';

  const firstPublishedAtBundle = useMemo(
    () =>
      microcopySetToResourceBundles(searchValuesMicrocopy ?? [])
        .first_published_at,
    [searchValuesMicrocopy]
  );

  const {
    optimisticValues,
    selectedIds,
    selectedCount,
    handleClear,
    handleSelectMany,
  } = useFlatFacet({
    getController: getReleaseDateController,
    fieldName: 'first_published_at',
    getValueId,
  });

  const getLabel = useCallback(
    (index: number) => {
      const range = DATE_RANGE_KEYS[index];
      return firstPublishedAtBundle?.[range.bundleKey] ?? range.label;
    },
    [firstPublishedAtBundle]
  );

  const isDark = variant === 'mobile';

  const treeItems = useMemo(
    () =>
      [...optimisticValues]
        .sort(
          (a, b) =>
            Date.parse(b.start.replace('@', 'T').replace(/\//g, '-')) -
            Date.parse(a.start.replace('@', 'T').replace(/\//g, '-'))
        )
        .map((v, index) => ({
          id: getValueId(v),
          label: getLabel(index),
          count: v.numberOfResults,
          disabled: v.numberOfResults === 0 && v.state !== 'selected',
        })),
    [optimisticValues, getLabel]
  );

  const content = (
    <TreeAccordion
      items={treeItems}
      selectionMode="multiple"
      selectedIds={selectedIds}
      onSelectMany={handleSelectMany}
      isDarkTheme={isDark}
      size={variant === 'sidebar' ? 'default' : 'sm'}
    />
  );

  if (variant === 'horizontal') {
    return (
      <FacetDropdown
        label={dateLabel}
        selectedCount={selectedCount}
        clearLabel={clearLabel}
        onClear={handleClear}
        className={className}
      >
        {content}
      </FacetDropdown>
    );
  }

  return (
    <FacetAccordion
      title={dateLabel}
      variant={variant}
      className={className}
      showClear={variant === 'drawer' && selectedCount > 0}
      onClear={handleClear}
      clearLabel={clearLabel}
      defaultOpen={variant !== 'drawer'}
      selectedCount={selectedCount}
      isOpen={isOpen}
      onToggle={onToggle}
    >
      {content}
    </FacetAccordion>
  );
};
